import { $getRoot, LexicalEditor } from "lexical";
import { fullyDecodeHtmlEntities, initEditorWithHtml } from "@/lib/htmlHelpers";
import {
  $convertFromMarkdownString,
  TRANSFORMERS,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  CODE,
  HEADING,
  LINK,
  UNORDERED_LIST,
  ORDERED_LIST
} from "@lexical/markdown";

// Define all markdown transformers we want to use
const MARKDOWN_TRANSFORMERS = [
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  CODE,
  HEADING,
  LINK,
  UNORDERED_LIST,
  ORDERED_LIST,
  ...TRANSFORMERS
];

/**
 * Populates the editor with an initial state from a content string.
 * Tries to parse as Lexical JSON first, then falls back to Markdown/Plain Text.
 * @param editor The Lexical editor instance.
 * @param contentString The string representing the editor state (JSON or Markdown/Plain Text).
 */
export function handleInitialState(editor: LexicalEditor, contentString?: string | null) {
  if (contentString && contentString.trim() !== "") {
    try {
      // Attempt to parse as JSON first (new format)
      const editorState = editor.parseEditorState(contentString);
      editor.setEditorState(editorState);
    } catch (jsonError) {
      // If JSON parsing fails, assume it's Markdown/Plain Text (old format)
      console.warn("Failed to parse content as JSON, attempting Markdown/Plain Text conversion:", jsonError);
      try {
        const cleanedMarkdown = contentString
          .replace(/ {2}\n/g, "\n") // Remove double spaces at line ends
          .replace(/\\([*_`])/g, "$1") // Unescape markdown characters
          .trim();

        editor.update(
          () => {
            $getRoot().clear(); // Clear previous state before converting
            $convertFromMarkdownString(cleanedMarkdown, MARKDOWN_TRANSFORMERS, undefined, true);
          },
          { tag: "history-merge" }
        );
        console.log("Initialized editor with Markdown/Plain Text state");
      } catch (markdownError) {
        console.error("Error parsing initial editor state from Markdown/Plain Text fallback:", markdownError);
        editor.update(() => {
          $getRoot().clear(); // Fallback to a blank state on double error
        });
      }
    }
  } else {
    // If no contentString, ensure editor is clear
    editor.update(() => {
      $getRoot().clear();
    });
    console.log("Initialized editor with empty/blank state.");
  }
}

const hasHtmlTags = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

/**
 * Hydrates the Lexical editor from stored event terms/description-like content:
 * Lexical JSON (legacy), HTML (preferred after save), or Markdown/plain text.
 */
export function initializeRichTextForEdit(editor: LexicalEditor, contentString?: string | null) {
  const decoded = fullyDecodeHtmlEntities(contentString ?? "").trim();
  if (!decoded) {
    editor.update(() => {
      $getRoot().clear();
    });
    return;
  }

  try {
    editor.setEditorState(editor.parseEditorState(decoded));
    return;
  } catch {
    /* not Lexical JSON */
  }

  if (hasHtmlTags(decoded)) {
    initEditorWithHtml(editor, decoded);
    return;
  }

  handleInitialState(editor, decoded);
}
